import { useState, useEffect, type FormEvent } from 'react';
import {
  createFileRoute,
  redirect,
  useNavigate,
  useSearch,
} from '@tanstack/react-router';
import { Box, Flex, Text, Spinner } from '@chakra-ui/react';
import { z } from 'zod';
import { zodValidator } from '@tanstack/zod-adapter';
import { useAuth } from '@/features/auth/authContext';
import { LogoSvg } from '@/components/svg/LogoSvg';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';

/**
 * /login の検索パラメータ。
 * ログイン成功後に戻る URL を保持する。
 */
const loginSearchSchema = z.object({
  redirect: z.string().optional().catch(undefined),
});

export const Route = createFileRoute('/login')({
  validateSearch: zodValidator(loginSearchSchema),
  beforeLoad: ({ context, search }) => {
    // 既にログイン済みならリダイレクト先（あれば）もしくは / へ
    if (context.auth.isAuthenticated) {
      throw redirect({ to: search.redirect ?? '/dashboard' });
    }
  },
  component: LoginPage,
});

function LoginPage() {
  const { login, error, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const search = useSearch({ from: '/login' });
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // onAuthStateChanged による Context 更新（再レンダリング）後にナビゲート
  useEffect(() => {
    if (isAuthenticated) {
      navigate({ to: search.redirect ?? '/dashboard' });
    }
  }, [isAuthenticated, navigate, search.redirect]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await login(email, password);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Flex minH="100vh" align="center" justify="center" bg="bg.DEFAULT">
      <Box w="full" maxW="400px" bg="bg.panel" p={8} borderRadius="lg" shadow="lg">
        <Flex align="center" justify="center" gap={2} mb={6}>
          <LogoSvg style={{ width: '96px', height: '17px' }} />
          <Text fontSize="lg" fontWeight="bold">管理コンソール</Text>
        </Flex>

        <form onSubmit={handleSubmit}>
          <Flex direction="column" gap={4}>
            {error && (
              <Box bg="admin.error.bg" color="fg.error" p={3} borderRadius="md" fontSize="sm">
                {error}
              </Box>
            )}

            <Box>
              <Text as="label" fontSize="sm" fontWeight="medium" mb={1}>
                メールアドレス
              </Text>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@caream.jp"
                required
                disabled={isSubmitting}
              />
            </Box>

            <Box>
              <Text as="label" fontSize="sm" fontWeight="medium" mb={1}>
                パスワード
              </Text>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="パスワードを入力"
                required
                disabled={isSubmitting}
              />
            </Box>

            <Button type="submit" colorPalette="brand" w="full" disabled={isSubmitting}>
              {isSubmitting ? (
                <Flex align="center" justify="center" gap={2}>
                  <Spinner size="sm" />
                  ログイン中...
                </Flex>
              ) : (
                'ログイン'
              )}
            </Button>
          </Flex>
        </form>
      </Box>
    </Flex>
  );
}
